// La grilla de servicios de /services. Desde el 03-11-2026 la usa solo
// /services: la home volvió a su grilla propia porque Diego no quiso que se
// tocara (ver SERVICIOS-GRILLA-UNIFICADA). Dos variantes:
//   destacado — tarjeta azul grande, 4 columnas ("Los más pedidos");
//   compacto  — fila blanca con ícono a la izquierda, 3 columnas ("Todos los servicios").
// La tarjeta entera es un <a>, sin botón. El amarillo va solo en el borde, al
// pasar el mouse. Editar y Eliminar del admin van fuera del <a>, en la esquina,
// para que no disparen la navegación.
import { Box, IconButton, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink } from 'react-router-dom';
import { rutaDeServicio } from '../utils/servicios';
import { iconoDeServicio, iconoDestacado } from '../utils/iconoServicio';

const font = "'Montserrat', sans-serif";

const COLUMNAS = {
  destacado: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
  compacto:  { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
};

// Colores por variante. Contraste sobre #163244 (WCAG): título #E7A432 6,2:1;
// categoría #CFDBDE 9,4:1 (el gris azulado claro medido en la maqueta de Diego,
// 03-11-2026); #F87171 4,8:1.
const COLORES = {
  destacado: {
    fondo: '#163244', titulo: '#E7A432', categoria: '#CFDBDE',
    aviso: '#F87171', editar: '#fff', eliminar: '#F87171',
    // En azul, un borde dorado de 1 px casi no se ve: va de 2 px, transparente
    // en reposo para que la tarjeta no salte al pasar el mouse.
    borde: '2px solid transparent', bordeHover: '#E7A432',
  },
  compacto: {
    fondo: '#fff', titulo: '#163244', categoria: '#7a93a4',
    aviso: '#EF4444', editar: '#163244', eliminar: '#EF4444',
    borde: '1px solid rgba(0,0,0,0.08)', bordeHover: '#E7A432',
  },
};

const tarjetaSx = (variante, conAdmin) => {
  const c = COLORES[variante];
  return {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    boxSizing: 'border-box',
    bgcolor: c.fondo,
    border: c.borde,
    borderRadius: '14px',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'border-color 0.15s',
    ...(variante === 'destacado'
      // Más baja desde el 03-11-2026 (Diego): ocupaba casi lo que tres filas compactas.
      ? { flexDirection: 'column', justifyContent: 'center', textAlign: 'center', gap: 1, p: 2, minHeight: 130 }
      : { gap: 1.5, px: 2, py: 1.5, pr: conAdmin ? 9 : 2 }),
    '&:hover, &:focus-visible': { borderColor: c.bordeHover, outline: 'none' },
  };
};

// El ícono de los destacados, según la maqueta de Diego (03-11-2026): directo
// sobre el azul, sin marco, con el contorno exterior dorado (icons/destacados/,
// generado por script) y al 70% de opacidad, puesta acá y no en el archivo.
// El dibujo ocupa cerca de la mitad del lienzo del PNG: con la imagen al 50%
// del ancho útil, el dibujo queda en ~22% del ancho de la tarjeta (era ~30%,
// como en la maqueta; Diego lo pidió más chico el 03-11-2026). Los márgenes
// negativos se comen parte del margen transparente de arriba y de abajo (~26%
// del alto de la imagen por lado), para que la tarjeta no crezca de más.
const OPACIDAD_ICONO_DESTACADO = 0.7;
const iconoDestacadoSx = {
  width: '50%', maxWidth: 110, height: 'auto', aspectRatio: '1 / 1', objectFit: 'contain',
  my: '-10%', opacity: OPACIDAD_ICONO_DESTACADO, flexShrink: 0,
};

// copia: la tarjeta repetida del carrusel (CarruselDestacados) para el bucle.
// No toma foco ni lleva los botones del admin; el aria-hidden va en su contenedor.
export function Tarjeta({ servicio, variante, esAdmin, onEditar, onEliminar, copia = false }) {
  const ruta = rutaDeServicio(servicio);
  const grande = variante === 'destacado';
  const c = COLORES[variante];
  const conAdmin = esAdmin && !copia;

  const imagen = grande
    ? <Box component="img" src={iconoDestacado(servicio.icon)} alt="" draggable={false} sx={iconoDestacadoSx} />
    : <Box component="img" src={iconoDeServicio(servicio.icon)} alt=""
        sx={{ width: 34, height: 34, objectFit: 'contain', flexShrink: 0 }} />;

  const contenido = (
    <>
      {imagen}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: font, fontWeight: 700, color: c.titulo, fontSize: grande ? 15 : 13.5, lineHeight: 1.3 }}>
          {servicio.title}
        </Typography>
        {servicio.category && (
          <Typography sx={{ fontFamily: font, fontSize: 11.5, color: c.categoria, mt: 0.3 }}>
            {servicio.category}
          </Typography>
        )}
        {!ruta && (
          <Typography sx={{ fontFamily: font, fontSize: 11, fontWeight: 700, color: c.aviso, mt: 0.3 }}>
            sin slug: los clientes no lo ven
          </Typography>
        )}
      </Box>
    </>
  );

  // Sin slug solo llega el admin: se muestra marcado y no navega.
  const tarjeta = ruta
    ? (
      <Box component={RouterLink} to={ruta} state={{ serviceTitle: servicio.title }} draggable={false}
        tabIndex={copia ? -1 : undefined} sx={tarjetaSx(variante, conAdmin)}>
        {contenido}
      </Box>
    )
    : <Box sx={{ ...tarjetaSx(variante, conAdmin), borderStyle: 'dashed', borderColor: c.aviso }}>{contenido}</Box>;

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      {tarjeta}
      {conAdmin && (
        <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.25 }}>
          <IconButton size="small" title="Editar" aria-label={`Editar ${servicio.title}`} onClick={() => onEditar(servicio)}
            sx={{ color: c.editar }}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Eliminar" aria-label={`Eliminar ${servicio.title}`} onClick={() => onEliminar(servicio)}
            sx={{ color: c.eliminar }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

export default function ServiceGrid({ services, variante = 'compacto', esAdmin = false, onEditar, onEliminar }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNAS[variante], gap: 1.5 }}>
      {services.map(s => (
        <Tarjeta key={s.id} servicio={s} variante={variante} esAdmin={esAdmin}
          onEditar={onEditar} onEliminar={onEliminar} />
      ))}
    </Box>
  );
}
